import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';

interface Citation {
  id: string;
  file_path?: string;
  page?: number[];
  chunk_index?: number;
  text_content?: string;
  fund?: string;
}

interface ChatMessageProps {
  type: 'user' | 'assistant';
  text: string;
  citations?: Citation[];
  onCitationClick: (citation: Citation, page: number) => void;
}

const ChatMessage: React.FC<ChatMessageProps> = ({ type, text, citations, onCitationClick }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch (err) {
      console.error('Failed to copy message:', err);
    }
  };

  if (type === 'user') {
    return (
      <div className="flex items-start justify-end">
        <div className="bg-gray-100 text-gray-900 rounded-3xl px-4 py-3 max-w-[80%]">
          <p className="text-sm whitespace-pre-wrap">{text}</p>
        </div>
      </div>
    );
  }

  // Replaces `[1]` / `[1,2,3]` tokens inside a plain-text string with clickable
  // citation pills. Called for every string node produced by react-markdown so
  // bold/italic/list markdown continues to render natively around the pills.
  const renderTextWithCitations = (rawText: string): React.ReactNode[] => {
    const parts = rawText.split(/(\[(?:\d+\s*(?:,\s*\d+\s*)*)\])/g);
    return parts.map((part, i) => {
      const match = part.match(/^\[(\d+(?:\s*,\s*\d+)*)\]$/);
      if (match && citations && citations.length > 0) {
        const chunkIndexes = match[1]
          .split(',')
          .map((n) => parseInt(n.trim(), 10))
          .filter((n) => !Number.isNaN(n));

        const rendered: React.ReactNode[] = [<span key={`${i}-open`}>[</span>];
        chunkIndexes.forEach((chunkIndex, idx) => {
          const citation = citations.find((c) => c.chunk_index === chunkIndex);
          if (citation) {
            const page = citation.page && citation.page.length > 0 ? citation.page[0] : 1;
            rendered.push(
              <button
                key={`${i}-${chunkIndex}`}
                onClick={() => onCitationClick(citation, page)}
                className="inline-flex items-center justify-center w-5 h-5 text-[10px] font-bold text-blue-700 bg-blue-100 rounded-full hover:bg-blue-200 transition-colors mx-0.5 align-text-top cursor-pointer"
                title={`Citation ${chunkIndex}`}
              >
                {chunkIndex}
              </button>,
            );
          } else {
            rendered.push(<span key={`${i}-missing-${chunkIndex}`}>{chunkIndex}</span>);
          }

          if (idx < chunkIndexes.length - 1) {
            rendered.push(<span key={`${i}-comma-${idx}`}>,</span>);
          }
        });
        rendered.push(<span key={`${i}-close`}>]</span>);
        return <React.Fragment key={i}>{rendered}</React.Fragment>;
      }
      return <span key={i}>{part}</span>;
    });
  };

  // Walk react-markdown's rendered children and replace any plain-string
  // descendant with citation-aware nodes. Non-string children (e.g. <strong>)
  // are left untouched — their own renderer will recurse.
  const withCitations = (children: React.ReactNode): React.ReactNode => {
    return React.Children.map(children, (child, idx) => {
      if (typeof child === 'string') {
        return <React.Fragment key={idx}>{renderTextWithCitations(child)}</React.Fragment>;
      }
      return child;
    });
  };

  const markdownComponents = {
    p: ({ children }: { children?: React.ReactNode }) => (
      <p className="mb-3 last:mb-0 leading-relaxed">{withCitations(children)}</p>
    ),
    strong: ({ children }: { children?: React.ReactNode }) => (
      <strong className="font-semibold text-gray-900">{withCitations(children)}</strong>
    ),
    em: ({ children }: { children?: React.ReactNode }) => (
      <em>{withCitations(children)}</em>
    ),
    ul: ({ children }: { children?: React.ReactNode }) => (
      <ul className="list-disc pl-6 mb-3 space-y-1">{children}</ul>
    ),
    ol: ({ children }: { children?: React.ReactNode }) => (
      <ol className="list-decimal pl-6 mb-3 space-y-1">{children}</ol>
    ),
    li: ({ children }: { children?: React.ReactNode }) => (
      <li className="leading-relaxed">{withCitations(children)}</li>
    ),
    h1: ({ children }: { children?: React.ReactNode }) => (
      <h1 className="text-lg font-semibold mt-3 mb-2 text-gray-900">{withCitations(children)}</h1>
    ),
    h2: ({ children }: { children?: React.ReactNode }) => (
      <h2 className="text-base font-semibold mt-3 mb-2 text-gray-900">{withCitations(children)}</h2>
    ),
    h3: ({ children }: { children?: React.ReactNode }) => (
      <h3 className="text-sm font-semibold mt-3 mb-1.5 text-gray-900">{withCitations(children)}</h3>
    ),
    h4: ({ children }: { children?: React.ReactNode }) => (
      <h4 className="text-sm font-semibold mt-2 mb-1 text-gray-900">{withCitations(children)}</h4>
    ),
    blockquote: ({ children }: { children?: React.ReactNode }) => (
      <blockquote className="border-l-4 border-gray-200 pl-3 italic text-gray-600 mb-3">
        {children}
      </blockquote>
    ),
    code: ({ children, className }: { children?: React.ReactNode; className?: string }) => {
      const isBlock = Boolean(className);
      if (isBlock) {
        return (
          <pre className="bg-gray-50 border border-gray-200 rounded-md p-3 overflow-x-auto text-xs mb-3">
            <code className={className}>{children}</code>
          </pre>
        );
      }
      return (
        <code className="bg-gray-100 px-1 py-0.5 rounded text-[0.85em] font-mono">
          {children}
        </code>
      );
    },
    a: ({ children, href }: { children?: React.ReactNode; href?: string }) => (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="text-blue-600 hover:underline break-words"
      >
        {withCitations(children)}
      </a>
    ),
    hr: () => <hr className="my-3 border-gray-200" />,
  };

  return (
    <div className="flex items-start">
      <div className="rounded-2xl px-1 py-1 max-w-[80%] min-w-0">
        <div className="text-sm text-gray-800 leading-relaxed break-words">
          <ReactMarkdown components={markdownComponents}>{text}</ReactMarkdown>
        </div>

        <div className="mt-3 pt-2 border-t border-gray-200">
          <div className="flex items-center justify-between mb-1.5">
            <p className="text-xs text-gray-500">{citations && citations.length > 0 ? 'Sources:' : ''}</p>
            <button
              onClick={handleCopy}
              className="p-1.5 rounded border border-gray-200 text-gray-600 hover:bg-gray-100 transition-colors"
              title="Copy answer"
              aria-label="Copy answer"
            >
              {copied ? (
                <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2M10 20h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              )}
            </button>
          </div>
          {citations && citations.length > 0 && (
            <div className="flex flex-wrap gap-1.5 min-w-0">
              {[...citations]
                .sort((a, b) => {
                  const ai = typeof a.chunk_index === 'number' ? a.chunk_index : Number.POSITIVE_INFINITY;
                  const bi = typeof b.chunk_index === 'number' ? b.chunk_index : Number.POSITIVE_INFINITY;
                  return ai - bi;
                })
                .map((c, idx) => {
                  const page = c.page && c.page.length > 0 ? c.page[0] : 1;
                  const label = c.file_path ? c.file_path.split('/').pop() : `Source ${idx + 1}`;
                  return (
                    <button
                      key={c.id || idx}
                      onClick={() => onCitationClick(c, page)}
                      className="text-xs bg-white border border-gray-200 rounded-md px-2 py-1 hover:bg-blue-50 hover:border-blue-300 transition-colors text-gray-600"
                      title={label}
                    >
                      [{c.chunk_index}]
                    </button>
                  );
                })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChatMessage;
